// PaystackIframe.tsx
import React, { FC, useEffect, useRef, useState } from "react";
import './PaystackIframe.css'; // optional: стилі для модалки/бекдропа
import Loader from "../Lobby/Loader";

interface PaystackIframeProps {
    sessionToken?: string;
    triggerRedirectToTrack?: () => void;
    clearPaystackSession?: () => void;
    onError?: (err?: any) => void;
    autoCloseOnSuccess?: boolean;
    sandbox?: string;
}

const PaystackIframe: FC<PaystackIframeProps> = ({
                                                     sessionToken,
                                                     triggerRedirectToTrack,
                                                     clearPaystackSession,
                                                     onError,
                                                     autoCloseOnSuccess = true,
                                                     sandbox = "allow-forms allow-scripts allow-same-origin allow-popups"
                                                 }) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        container.innerHTML = "";
        iframeRef.current = null;
        setVisible(false);
        setLoading(false);

        if (!sessionToken || sessionToken.length === 0) {
            return;
        }

        try {
            setLoading(true);

            const iframe = document.createElement("iframe");
            iframeRef.current = iframe;

            iframe.src = `https://checkout.paystack.com/${encodeURIComponent(sessionToken)}`;


            iframe.width = "400";
            iframe.height = "600";
            iframe.style.border = "0";
            iframe.style.maxWidth = "480px";
            iframe.style.minHeight = "500px";
            iframe.style.display = "block";
            iframe.style.borderRadius = "8px";
            iframe.setAttribute("title", "Paystack Checkout");


            if (sandbox) iframe.setAttribute("sandbox", sandbox);


            const onLoad = () => {
                setLoading(false);
                setVisible(true);
            };
            iframe.addEventListener("load", onLoad);


            container.appendChild(iframe);


            return () => {
                iframe.removeEventListener("load", onLoad);
                if (container.contains(iframe)) container.removeChild(iframe);
                iframeRef.current = null;
                setVisible(false);
                setLoading(false);
            };
        } catch (err) {
            console.error("Paystack iframe create error:", err);
            onError?.(err);
            setLoading(false);
            setVisible(false);

            if (container) container.innerHTML = "";
        }
    }, [sessionToken, sandbox, onError]);

    useEffect(() => {
        const prev = document.body.style.overflow;
        if (visible) document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev || "";
        };
    }, [visible]);

    useEffect(() => {
        const allowedOrigins = new Set([
            'https://checkout.paystack.com'
        ]);
        const handledRef = { current: false };

        const handler = (event: MessageEvent) => {
            try {
                if (!event || !event.data) return;
                if (typeof event.data === 'object' && (event.data as any).source === 'react-devtools-bridge') {
                    return;
                }
                if (!allowedOrigins.has(event.origin)) {
                    console.debug('Ignored message from origin', event.origin);
                    return;
                }
                const iframeEl = containerRef?.current?.querySelector('iframe');
                const iframeWindow = iframeEl?.contentWindow;
                if (iframeWindow && event.source !== iframeWindow) {
                    console.debug('Ignored message: source !== iframeWindow');
                    return;
                }
                let message: any = event.data;
                if (typeof message === 'string') {
                    try { message = JSON.parse(message); } catch (e) {}
                }
                if (message?.payload) message = message.payload;
                if (message?.data) message = message.data;

                console.debug('Paystack normalized message:', message);
                const redirectUrl = message?.redirecturl || message?.redirectUrl || message?.redirect;
                if (redirectUrl) {
                    console.info('Intercepted redirecturl from paystack message:', redirectUrl);
                    try {
                        event.stopImmediatePropagation?.();
                        event.preventDefault?.();
                    } catch (err) {
                        console.error(err);
                    }

                    if (!handledRef.current) {
                        handledRef.current = true;
                        if (containerRef.current) containerRef.current.innerHTML = '';
                        setVisible(false);
                        triggerRedirectToTrack?.()
                    }
                    return;
                }
                if (event.data === 'close') {
                    clearPaystackSession?.();
                    if (containerRef.current) containerRef.current.innerHTML = '';
                    setVisible(false);
                    return;
                }
                if (message.status === 'error') {
                    onError?.(message);
                    return;
                }
                console.debug('Unrecognized paystack message', message);
            } catch (err) {
                console.error('Error in postMessage handler', err);
            }
        };

        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [triggerRedirectToTrack, clearPaystackSession, onError]);


    const close = () => {
        if (containerRef.current) containerRef.current.innerHTML = "";
        setVisible(false);
        clearPaystackSession?.();
    };

    return (
        <div
            id="paystack-checkout-wrapper"
            style={{
                display: visible ? "flex" : loading ? "flex" : "none", // показуємо і лоадер, і iframe
                position: "fixed",
                left: 0,
                top: 0,
                right: 0,
                bottom: 0,
                justifyContent: "center",
                alignItems: "center",
                background: "rgba(0,0,0,0.6)",
                zIndex: 9999,
                padding: 20,
                boxSizing: "border-box"
            }}
        >
            <div
                style={{
                    position: "relative",
                    width: "100%",
                    maxWidth: 520,
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                <button
                    onClick={close}
                    aria-label="Close payment"
                    style={{
                        position: "absolute",
                        right: -10,
                        top: -10,
                        zIndex: 10000,
                        borderRadius: "50%",
                        background: "#fff",
                        border: "none",
                        width: 36,
                        height: 36,
                        cursor: "pointer",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.2)"
                    }}
                >
                    ✕
                </button>

                <div
                    ref={containerRef}
                    style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center"
                    }}
                 />
            </div>
        </div>
    );
};

export default PaystackIframe;
